import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Shield, Zap, Lock, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SecurityPreferenceDialogProps {
  open: boolean;
  onComplete: () => void;
  userId: string;
}

export function SecurityPreferenceDialog({
  open,
  onComplete,
  userId,
}: SecurityPreferenceDialogProps) {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState<string>("standard");
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!selectedMethod) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          verification_method: selectedMethod,
          initial_verification_complete: true,
        })
        .eq("id", userId);

      if (error) throw error;

      toast.success(t("settings.securityPreferencesSaved"));
      
      // Wait a bit to ensure the database update is complete
      await new Promise(resolve => setTimeout(resolve, 300));
      
      onComplete();
    } catch (error: any) {
      console.error("Failed to save security preferences:", error);
      toast.error(t("common.error"));
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isSaving && !isOpen && onComplete()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="rounded-full bg-primary/10 p-3">
              <Shield className="w-8 h-8 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            {t("settings.chooseSecurityPreference")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t("settings.selectPreferredLoginMethod")}
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={selectedMethod}
          onValueChange={setSelectedMethod}
          className="space-y-3 mt-4"
        >
          {/* Standard Login */}
          <div
            className={`relative flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-all ${
              selectedMethod === "standard"
                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                : "border-border hover:border-primary/50"
            }`}
            onClick={() => setSelectedMethod("standard")}
          >
            <RadioGroupItem value="standard" id="standard" className="mt-1" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                <Label
                  htmlFor="standard"
                  className="text-base font-medium cursor-pointer"
                >
                  {t("settings.standardLogin")}
                </Label>
                <span className="text-xs bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full">
                  {t("settings.default")}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {t("settings.standardLoginDescription")}
              </p>
            </div>
          </div>

          {/* Magic Link */}
          <div
            className={`relative flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-all ${
              selectedMethod === "magic_link"
                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                : "border-border hover:border-primary/50"
            }`}
            onClick={() => setSelectedMethod("magic_link")}
          >
            <RadioGroupItem value="magic_link" id="magic_link" className="mt-1" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" />
                <Label
                  htmlFor="magic_link"
                  className="text-base font-medium cursor-pointer"
                >
                  {t("settings.magicLinkLogin")}
                </Label>
                <span className="text-xs bg-green-500/10 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full">
                  {t("settings.recommended")}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {t("settings.magicLinkDescription")}
              </p>
            </div>
          </div>

          {/* Always Verify */}
          <div
            className={`relative flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-all ${
              selectedMethod === "always_verify"
                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                : "border-border hover:border-primary/50"
            }`}
            onClick={() => setSelectedMethod("always_verify")}
          >
            <RadioGroupItem
              value="always_verify"
              id="always_verify"
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-primary" />
                <Label
                  htmlFor="always_verify"
                  className="text-base font-medium cursor-pointer"
                >
                  {t("settings.alwaysVerify")}
                </Label>
                <span className="text-xs bg-orange-500/10 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full">
                  {t("settings.mostSecure")}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {t("settings.alwaysVerifyDescription")}
              </p>
            </div>
          </div>
        </RadioGroup>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1"
            size="lg"
          >
            {isSaving ? t("common.saving") : t("common.continue")}
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground mt-4">
          {t("settings.canChangeInSettings")}
        </p>
      </DialogContent>
    </Dialog>
  );
}
